import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, Wind, Heart, ArrowLeft,
  Target, Moon, Brain, Focus, Wallet, Home, Footprints, Activity,
  Headphones, Sparkles, ClipboardList, ShoppingCart,
  FileText, Flame, Bell, Users, Shield, CheckCircle,
  Droplets, Smile, CalendarDays, Dumbbell, Apple, Briefcase,
  MessageCircle, Handshake, HeartHandshake, Baby, Flower2, Link2, AlertOctagon, HeartCrack, ClipboardCheck,
  Wrench, FolderKanban, Package, PiggyBank, CreditCard, FileSearch, FileUser, TrendingUp, UtensilsCrossed, HeartPulse,
  User, Gift, Calendar
} from 'lucide-react';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';

// All features organized by category
const CATEGORY_DATA = {
  thoughts: {
    title: 'Tankar & känslor',
    subtitle: 'Dagbok, känslor och självinsikt',
    emoji: '🧠',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    features: [
      { id: 'journal', title: 'Dagbok', desc: 'Skriv av dig och reflektera', icon: BookOpen, path: '/journal', emoji: '📝' },
      { id: 'reflections', title: 'Reflektioner', desc: 'Guidade frågor för självinsikt', icon: Sparkles, path: '/reflections', emoji: '✨' },
      { id: 'emotions', title: 'Känslor', desc: 'Förstå och hantera dina känslor', icon: Heart, path: '/emotions', emoji: '💜' },
      { id: 'mood', title: 'Humör', desc: 'Logga och följ ditt mående', icon: Smile, path: '/mood', emoji: '😊' },
      { id: 'gratitude', title: 'Tacksamhet', desc: 'Daglig tacksamhetsövning', icon: Gift, path: '/gratitude', emoji: '🙏' },
      { id: 'self-discovery', title: 'Självkännedom', desc: 'Lär känna dig själv bättre', icon: User, path: '/self-discovery', emoji: '🔍' },
      { id: 'attachment', title: 'Anknytning & mönster', desc: 'Förstå dina relationsmönster', icon: Link2, path: '/attachment', emoji: '🔗' },
      { id: 'selflove', title: 'Självkärlek', desc: 'Relationen till dig själv', icon: Flower2, path: '/self-love', emoji: '🌸' },
      { id: 'triggers', title: 'Triggers', desc: 'Identifiera vad som påverkar dig', icon: Target, path: '/triggers', emoji: '🎯' },
    ]
  },
  health: {
    title: 'Hälsa & kropp',
    subtitle: 'Träning, kost och välmående',
    emoji: '💪',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    features: [
      { id: 'fitness', title: 'Träning', desc: 'Övningar och program', icon: Dumbbell, path: '/fitness', emoji: '🏋️' },
      { id: 'nutrition', title: 'Kost & Recept', desc: 'Näringslära och recept', icon: Apple, path: '/nutrition', emoji: '🥗' },
      { id: 'meal-planner', title: 'Måltidsplanering', desc: 'Planera veckans måltider', icon: UtensilsCrossed, path: '/meal-planner', emoji: '🍽️' },
      { id: 'health-log', title: 'Hälsologg', desc: 'Mediciner, symtom & bokningar', icon: HeartPulse, path: '/health-log', emoji: '🏥' },
      { id: 'water', title: 'Vattenkoll', desc: 'Spåra vattenintag', icon: Droplets, path: '/water', emoji: '💧' },
      { id: 'sleep', title: 'Sömn', desc: 'Logga och förbättra sömnen', icon: Moon, path: '/sleep', emoji: '😴' },
      { id: 'steps', title: 'Steg', desc: 'Räkna dagliga steg', icon: Footprints, path: '/steps', emoji: '👟' },
      { id: 'hormones', title: 'Hormoncykel', desc: 'Spåra mens och symptom', icon: Activity, path: '/hormones', emoji: '🌙' },
    ]
  },
  calm: {
    title: 'Lugn & fokus',
    subtitle: 'Andning, meditation och fokus',
    emoji: '🧘',
    lightColor: 'bg-violet-50',
    textColor: 'text-violet-600',
    borderColor: 'border-violet-200',
    features: [
      { id: 'exercises', title: 'Andning', desc: 'Andnings- och avslappningsövningar', icon: Wind, path: '/exercises', emoji: '🌬️' },
      { id: 'meditation', title: 'Meditation', desc: 'Guidade meditationer med ljud', icon: Headphones, path: '/meditation', emoji: '🎧' },
      { id: 'focus', title: 'Fokus', desc: 'Verktyg för koncentration', icon: Focus, path: '/focus', emoji: '🎯' },
      { id: 'memory', title: 'Minnesträning', desc: 'Träna hjärnan med spel', icon: Brain, path: '/memory', emoji: '🧩' },
    ]
  },
  home: {
    title: 'Mitt hem',
    subtitle: 'Städ, rutiner och projekt',
    emoji: '🏠',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    features: [
      { id: 'cleaning', title: 'Städschema', desc: 'Håll koll på städningen', icon: ClipboardList, path: '/cleaning', emoji: '🧹' },
      { id: 'shopping', title: 'Inköpslistor', desc: 'Planera dina inköp', icon: ShoppingCart, path: '/shopping', emoji: '🛒' },
      { id: 'routines', title: 'Rutiner', desc: 'Morgon- och kvällsrutiner', icon: Home, path: '/routines', emoji: '⏰' },
      { id: 'maintenance', title: 'Hemunderhåll', desc: 'Service och underhåll', icon: Wrench, path: '/maintenance', emoji: '🔧' },
      { id: 'projects', title: 'Projekt', desc: 'Planera hemprojekt', icon: FolderKanban, path: '/projects', emoji: '📋' },
      { id: 'inventory', title: 'Inventarier', desc: 'Värdefulla saker', icon: Package, path: '/inventory', emoji: '📦' },
    ]
  },
  planning: {
    title: 'Planering',
    subtitle: 'Mål, vanor och kalender',
    emoji: '📅',
    lightColor: 'bg-sky-50',
    textColor: 'text-sky-600',
    borderColor: 'border-sky-200',
    features: [
      { id: 'week-planner', title: 'Veckoöversikt', desc: 'Se hela veckan', icon: CalendarDays, path: '/week-planner', emoji: '📆' },
      { id: 'todos', title: 'Att göra', desc: 'Lista och bocka av uppgifter', icon: CheckCircle, path: '/todos', emoji: '✅' },
      { id: 'goals', title: 'Mål', desc: 'Sätt och följ upp dina mål', icon: Target, path: '/goals', emoji: '🎯' },
      { id: 'habits', title: 'Vanor', desc: 'Bygg goda vanor dagligen', icon: Flame, path: '/habits', emoji: '🔥' },
      { id: 'important-dates', title: 'Viktiga datum', desc: 'Födelsedagar och årsdagar', icon: Calendar, path: '/important-dates', emoji: '🎂' },
      { id: 'reminders', title: 'Påminnelser', desc: 'Dagliga påminnelser', icon: Bell, path: '/reminders', emoji: '🔔' },
      { id: 'notes', title: 'Anteckningar', desc: 'Samla tankar och idéer', icon: FileText, path: '/notes', emoji: '📒' },
    ]
  },
  economy: {
    title: 'Ekonomi & karriär',
    subtitle: 'Budget, sparande och jobb',
    emoji: '💼',
    lightColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200',
    features: [
      { id: 'economy-check', title: 'Ekonomihälso-check', desc: 'Hitta dina fokusområden', icon: TrendingUp, path: '/economy-check', emoji: '📈' },
      { id: 'finance', title: 'Budget', desc: 'Håll koll på ekonomin', icon: Wallet, path: '/finance', emoji: '💰' },
      { id: 'savings', title: 'Sparmål', desc: 'Sätt och följ sparande', icon: PiggyBank, path: '/savings', emoji: '🐷' },
      { id: 'debts', title: 'Skulder', desc: 'Överblick och avbetalning', icon: CreditCard, path: '/debts', emoji: '💳' },
      { id: 'career', title: 'Karriär', desc: 'Mål, test och guider', icon: Briefcase, path: '/career', emoji: '🚀' },
      { id: 'job-search', title: 'Jobbsökning', desc: 'Tips och spårning', icon: FileSearch, path: '/job-search', emoji: '🔎' },
      { id: 'cv-help', title: 'CV-hjälp', desc: 'Guider för CV & brev', icon: FileUser, path: '/cv-help', emoji: '📄' },
    ]
  },
  relations: {
    title: 'Relationer',
    subtitle: 'Kommunikation, konflikt och kärlek',
    emoji: '❤️',
    lightColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    borderColor: 'border-rose-200',
    features: [
      { id: 'relationship-check', title: 'Relationshälso-check', desc: 'Hitta dina fokusområden', icon: ClipboardCheck, path: '/relationship-check', emoji: '💕' },
      { id: 'communication', title: 'Kommunikation', desc: 'Uttryck dig & lyssna', icon: MessageCircle, path: '/communication', emoji: '💬' },
      { id: 'conflict', title: 'Konflikthantering', desc: 'Hantera bråk konstruktivt', icon: Handshake, path: '/conflict', emoji: '🤝' },
      { id: 'couple-exercises', title: 'Parövningar', desc: 'Stärk er relation', icon: HeartHandshake, path: '/couple-exercises', emoji: '💑' },
      { id: 'family', title: 'Familj', desc: 'Föräldraskap & ursprung', icon: Baby, path: '/family', emoji: '👨‍👩‍👧' },
      { id: 'friendship', title: 'Vänskap', desc: 'Hitta och vårda vänskaper', icon: Users, path: '/friendship', emoji: '👯' },
      { id: 'boundaries', title: 'Gränser', desc: 'Lär dig sätta gränser', icon: Shield, path: '/boundaries', emoji: '🛡️' },
      { id: 'toxic', title: 'Toxiska relationer', desc: 'Identifiera destruktiva mönster', icon: AlertOctagon, path: '/toxic-relationships', emoji: '⚠️' },
      { id: 'separation', title: 'Separation', desc: 'Hantera avslut hälsosamt', icon: HeartCrack, path: '/separation', emoji: '💔' },
    ]
  },
};

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  // Restore scroll position for this category
  useScrollRestoration(`category-${categoryId}`);
  
  const category = CATEGORY_DATA[categoryId];
  
  if (!category) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2">Kategorin hittades inte</h1>
          <Button onClick={() => navigate('/dashboard')}>Tillbaka till start</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`category-page-${categoryId}`}>
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="rounded-full w-9 h-9 p-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xl">{category.emoji}</span>
            <h1 className="font-heading font-semibold text-lg">{category.title}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {/* Features Grid - 2 columns */}
        <div className="grid grid-cols-2 gap-3">
          {category.features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
            <button
              key={feature.id}
              onClick={() => navigate(feature.path)}
              className={`bg-gradient-to-br ${category.lightColor} to-white p-4 rounded-2xl text-left group border ${category.borderColor} hover:shadow-lg transition-all duration-200`}
              data-testid={`feature-${feature.id}`}
            >
              <div className={`w-9 h-9 rounded-lg ${category.lightColor} flex items-center justify-center mb-2`}>
                <FeatureIcon className={`w-[18px] h-[18px] ${category.textColor}`} />
              </div>
              <h3 className="font-semibold text-[15px] text-foreground group-hover:text-primary transition-colors leading-tight">
                {feature.title}
              </h3>
              <p className="text-[12px] text-foreground/55 mt-1 leading-snug">
                {feature.desc}
              </p>
            </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
